import RapportCaisse from './pages/RapportCaisse';
import Rapports from './pages/Rapports';
import Fournisseurs from './pages/Fournisseurs';
import BonsCommande from './pages/BonsCommande';
import React, { useState, useEffect } from 'react';
import { authService } from './services/auth.service';
import api from './services/api';
import { logoService } from './services/logo.service';
import Boutiques from './pages/Boutiques';
import Produits from './pages/Produits';
import Clients from './pages/Clients';
import Caisse from './pages/Caisse';
import Factures from './pages/Factures';
import Tresorerie from './pages/Tresorerie';
import Utilisateurs from './pages/Utilisateurs';
import Parametres from './pages/Parametres';
import Securite from './pages/Securite';
import ProtectionRole from './components/ProtectionRole';
import MouvementsStock from './pages/MouvementsStock';
import Etiquettes from './pages/Etiquettes';
import Notifications from './components/Notifications';
import Inventaire from './pages/Inventaire';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie,
  Cell, LineChart, Line, Legend
} from 'recharts';
import './Dashboard.css';

const COULEURS = {
  ordinaire: '#185FA5',
  revendeur: '#1D9E75',
  technicien: '#EF9F27',
};

function Dashboard() {
  const user = authService.getUser();
  const [pageCourante, setPageCourante] = useState('dashboard');
  // Tiroir latéral (mobile/tablette < 992 px).
  const [menuOuvert, setMenuOuvert] = useState(false);
  const [statsDB, setStatsDB] = useState({
    ventesJour: 0,
    caJour: 0,
    totalClients: 0,
    ruptures: 0,
  });
  const [stats7Jours, setStats7Jours] = useState<any[]>([]);
  const [statsClients, setStatsClients] = useState<any>(null);
  const [statsMois, setStatsMois] = useState<any[]>([]);
  const [chargement, setChargement] = useState(true);
  const [logo, setLogo] = useState<string | null>(logoService.getLogo());

  useEffect(() => {
    chargerTout();
    // Récupère le logo partagé depuis le serveur (puis met à jour le cache local)
    logoService.synchroniser().then(setLogo);
  }, []);

  const chargerTout = async () => {
    try {
      const [ventesRes, clientsRes, produitsRes, jours, clients, mois] = await Promise.all([
        api.get('/ventes/stats/jour'),
        api.get('/clients/stats'),
        api.get('/produits/stats'),
        api.get('/ventes/stats/7jours'),
        api.get('/ventes/stats/clients'),
        api.get('/ventes/stats/mois'),
      ]);
      setStatsDB({
        ventesJour: ventesRes.data.nombreVentes,
        caJour: ventesRes.data.chiffreAffaires,
        totalClients: clientsRes.data.total,
        ruptures: produitsRes.data.enRupture,
      });
      setStats7Jours(jours.data);
      setStatsClients(clients.data);
      setStatsMois(mois.data);
    } catch (e) {
    } finally {
      setChargement(false);
    }
  };

  // Point 4 — Confirmation avant déconnexion
  const handleLogout = () => {
    const confirmation = window.confirm(
      'Voulez-vous vraiment vous déconnecter de NexaCom ?\n\nAssurez-vous d\'avoir terminé toutes vos opérations en cours.'
    );
    if (confirmation) {
      authService.logout();
      window.location.href = '/';
    }
  };

  const navItem = (page: string, icon: string, label: string) => (
    <div
      className={`nav-item ${pageCourante === page ? 'active' : ''}`}
      onClick={() => { setPageCourante(page); setMenuOuvert(false); }}
    >
      <span>{icon}</span> {label}
    </div>
  );

  const fmt = (n: number) => Math.round(Number(n)).toLocaleString('fr-FR');

  const pieData = statsClients ? [
    { name: 'Ordinaire', value: statsClients.ordinaire?.total || 0, color: COULEURS.ordinaire },
    { name: 'Revendeur', value: statsClients.revendeur?.total || 0, color: COULEURS.revendeur },
    { name: 'Technicien', value: statsClients.technicien?.total || 0, color: COULEURS.technicien },
  ] : [];

  const totalPie = pieData.reduce((s, d) => s + d.value, 0);

  return (
    <div className="dashboard">
      {/* BARRE SUPÉRIEURE (mobile/tablette) */}
      <div className="dashboard-topbar">
        <button className="topbar-burger" onClick={() => setMenuOuvert(true)} aria-label="Ouvrir le menu">☰</button>
        <span className="topbar-titre">
          {logo ? <img src={logo} alt="Logo" /> : 'NexaCom'}
        </span>
      </div>

      {/* Overlay du tiroir */}
      {menuOuvert && <div className="dashboard-overlay" onClick={() => setMenuOuvert(false)} />}

      {/* SIDEBAR */}
      <div className={`dashboard-sidebar ${menuOuvert ? 'ouvert' : ''}`}>
        <div className="sidebar-logo">
          {logo ? (
            <img
              src={logo}
              alt="Logo"
              style={{ maxHeight: 36, maxWidth: 160, objectFit: 'contain', marginBottom: 2 }}
            />
          ) : (
            <h2>NexaCom</h2>
          )}
          <span>Gestion commerciale</span>
        </div>

        {/* Point 3 — Navigation filtrée par rôle */}
        <nav className="sidebar-nav">
          <div className="nav-section">Principal</div>
          {navItem('dashboard', '📊', 'Tableau de bord')}
          {navItem('caisse', '🛒', 'Caisse')}

          <div className="nav-section">Gestion</div>
          {navItem('stocks', '📦', 'Stocks & Produits')}
          {navItem('etiquettes', '🏷️', 'Étiquettes')}
          {navItem('mouvements', '📋', 'Mouvements stock')}
          {navItem('inventaire', '📦', 'Inventaire')}
          {navItem('clients', '👥', 'Clients')}
          {navItem('factures', '📄', 'Factures')}
          
          {(user?.role === 'admin' || user?.role === 'gerant') &&
  navItem('rapports', '📈', 'Rapports & stats')}
          {(user?.role === 'admin' || user?.role === 'gerant') &&
  navItem('rapport', '📋', 'Rapport de caisse')}
          {(user?.role === 'admin' || user?.role === 'gerant') &&
            navItem('boutiques', '🏪', 'Boutiques')}
            {(user?.role === 'admin' || user?.role === 'gerant') &&
  navItem('fournisseurs', '🏭', 'Fournisseurs')}
            {(user?.role === 'admin' || user?.role === 'gerant') &&
  navItem('commandes', '📦', 'Bons de commande')}

          {(user?.role === 'admin' || user?.role === 'gerant') && (
            <>
              <div className="nav-section">Finance</div>
              {navItem('tresorerie', '💰', 'Trésorerie')}
              {navItem('depenses', '💸', 'Dépenses')}
            </>
          )}

          {user?.role === 'admin' && (
            <>
              <div className="nav-section">Administration</div>
              {navItem('utilisateurs', '👤', 'Utilisateurs')}
              {navItem('parametres', '⚙️', 'Paramètres')}
            </>
          )}

          <div className="nav-section">Compte</div>
          {navItem('securite', '🔒', 'Sécurité')}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.prenom?.[0]}{user?.nom?.[0]}
            </div>
            <div className="user-details">
              <div className="user-name">{user?.prenom} {user?.nom}</div>
              <div className="user-role">{user?.role}</div>
            </div>
          </div>
          <button className="btn-logout" onClick={handleLogout}>
            Déconnexion
          </button>
          <div className="sidebar-notif">
  <Notifications />
</div>
        </div>
      </div>

      {/* CONTENU PRINCIPAL */}
      <div className="dashboard-main">

        {/* ===== TABLEAU DE BORD ===== */}
        {pageCourante === 'dashboard' && (
          <div className="db-content">
            <div className="db-header">
              <div>
                <h1>Tableau de bord</h1>
                <p>Bonjour {user?.prenom}, voici votre activité du jour</p>
              </div>
              <div className="db-date">
                {new Date().toLocaleDateString('fr-FR', {
                  weekday: 'long', year: 'numeric',
                  month: 'long', day: 'numeric'
                })}
              </div>
            </div>

            {/* KPI */}
            <div className="kpi-grid">
              <div className="kpi-card bleu">
                <div className="kpi-left">
                  <div className="kpi-label">CA aujourd'hui</div>
                  <div className="kpi-val">{fmt(statsDB.caJour)} FCFA</div>
                  <div className="kpi-sub">{statsDB.ventesJour} vente{statsDB.ventesJour > 1 ? 's' : ''}</div>
                </div>
                <div className="kpi-icon">💰</div>
              </div>
              <div className="kpi-card vert">
                <div className="kpi-left">
                  <div className="kpi-label">Ventes du jour</div>
                  <div className="kpi-val">{statsDB.ventesJour}</div>
                  <div className="kpi-sub">transactions enregistrées</div>
                </div>
                <div className="kpi-icon">🛒</div>
              </div>
              <div className="kpi-card orange">
                <div className="kpi-left">
                  <div className="kpi-label">Clients actifs</div>
                  <div className="kpi-val">{statsDB.totalClients}</div>
                  <div className="kpi-sub">dans la base de données</div>
                </div>
                <div className="kpi-icon">👥</div>
              </div>
              <div className={`kpi-card ${statsDB.ruptures > 0 ? 'rouge' : 'vert'}`}>
                <div className="kpi-left">
                  <div className="kpi-label">Ruptures de stock</div>
                  <div className="kpi-val">{statsDB.ruptures}</div>
                  <div className="kpi-sub">
                    {statsDB.ruptures > 0 ? 'produits à réapprovisionner' : 'tout est en stock'}
                  </div>
                </div>
                <div className="kpi-icon">⚠️</div>
              </div>
            </div>

            {/* GRAPHIQUES */}
            <div className="charts-row">
              {/* BARRES 7 JOURS */}
              <div className="chart-card large">
                <div className="chart-titre">
                  <span>Ventes — 7 derniers jours</span>
                  <span className="chart-total">
                    Total : {fmt(stats7Jours.reduce((s, j) => s + j.total, 0))} FCFA
                  </span>
                </div>
                {chargement ? (
                  <div className="chart-loading">Chargement...</div>
                ) : stats7Jours.every(j => j.total === 0) ? (
                  <div className="chart-empty">
                    <div>📊</div>
                    <p>Aucune vente sur les 7 derniers jours.<br />Commencez à encaisser depuis la Caisse !</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={stats7Jours} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1EFE8" />
                      <XAxis dataKey="jour" tick={{ fontSize: 12, fill: '#888' }} />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#888' }}
                        tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                      />
                      <Tooltip
                        formatter={(val: any, name: any) => [
                          `${fmt(val)} FCFA`,
                          name === 'ordinaire' ? 'Ordinaire' :
                          name === 'revendeur' ? 'Revendeur' : 'Technicien'
                        ]}
                      />
                      <Legend formatter={v =>
                        v === 'ordinaire' ? 'Ordinaire' :
                        v === 'revendeur' ? 'Revendeur' : 'Technicien'
                      } />
                      <Bar dataKey="ordinaire" stackId="a" fill={COULEURS.ordinaire} />
                      <Bar dataKey="revendeur" stackId="a" fill={COULEURS.revendeur} />
                      <Bar dataKey="technicien" stackId="a" fill={COULEURS.technicien} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* CAMEMBERT PAR TYPE CLIENT */}
              <div className="chart-card small">
                <div className="chart-titre">
                  <span>Par type de client</span>
                </div>
                {chargement ? (
                  <div className="chart-loading">Chargement...</div>
                ) : totalPie === 0 ? (
                  <div className="chart-empty">
                    <div>🥧</div>
                    <p>Aucune donnée disponible</p>
                  </div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%" cy="50%"
                          innerRadius={50} outerRadius={80}
                          dataKey="value"
                          paddingAngle={3}>
                          {pieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(val: any) => [`${fmt(val)} FCFA`]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="pie-legende">
                      {pieData.map((d, i) => (
                        <div key={i} className="pie-leg-row">
                          <div className="pie-dot" style={{ background: d.color }} />
                          <span className="pie-nom">{d.name}</span>
                          <span className="pie-val">{fmt(d.value)} F</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ÉVOLUTION MENSUELLE */}
            <div className="chart-card full">
              <div className="chart-titre">
                <span>Évolution mensuelle du CA</span>
                <span className="chart-total">5 derniers mois</span>
              </div>
              {chargement ? (
                <div className="chart-loading">Chargement...</div>
              ) : statsMois.every(m => m.total === 0) ? (
                <div className="chart-empty">
                  <div>📈</div>
                  <p>Pas encore de données mensuelles</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={statsMois} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1EFE8" />
                    <XAxis dataKey="mois" tick={{ fontSize: 12, fill: '#888' }} />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#888' }}
                      tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                    />
                    <Tooltip formatter={(val: any) => [`${fmt(val)} FCFA`, 'CA mensuel']} />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke="#C2410C"
                      strokeWidth={2.5}
                      dot={{ fill: '#C2410C', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* ACTIONS RAPIDES */}
            <div className="actions-rapides">
              <div className="ar-titre">Actions rapides</div>
              <div className="ar-grid">
                <div className="ar-btn" onClick={() => setPageCourante('caisse')}>
                  <span>🛒</span> Ouvrir la caisse
                </div>
                <div className="ar-btn" onClick={() => setPageCourante('stocks')}>
                  <span>📦</span> Gérer les stocks
                </div>
                <div className="ar-btn" onClick={() => setPageCourante('clients')}>
                  <span>👥</span> Voir les clients
                </div>
                <div className="ar-btn" onClick={() => setPageCourante('factures')}>
                  <span>📄</span> Historique ventes
                </div>
                {(user?.role === 'admin' || user?.role === 'gerant') && (
                  <div className="ar-btn" onClick={() => setPageCourante('tresorerie')}>
                    <span>💰</span> Trésorerie
                  </div>
                )}
                {(user?.role === 'admin' || user?.role === 'gerant') && (
                  <div className="ar-btn" onClick={() => setPageCourante('depenses')}>
                    <span>💸</span> Dépenses
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== MODULES ===== */}
        {pageCourante === 'caisse' && <Caisse />}
        {pageCourante === 'stocks' && <Produits />}
        {pageCourante === 'clients' && <Clients />}
        {pageCourante === 'factures' && <Factures />}
        {pageCourante === 'mouvements' && <MouvementsStock />}
        {pageCourante === 'etiquettes' && <Etiquettes />}
        {pageCourante === 'inventaire' && <Inventaire />}

        {/* Point 3 — Protection par rôle */}
        {pageCourante === 'boutiques' && (
          <ProtectionRole rolesAutorises={['admin', 'gerant']}>
            <Boutiques />
          </ProtectionRole>
        )}
        {pageCourante === 'tresorerie' && (
          <ProtectionRole rolesAutorises={['admin', 'gerant']}>
            <Tresorerie />
          </ProtectionRole>
        )}
        {pageCourante === 'depenses' && (
          <ProtectionRole rolesAutorises={['admin', 'gerant']}>
            <Tresorerie />
          </ProtectionRole>
        )}
        {pageCourante === 'utilisateurs' && (
          <ProtectionRole rolesAutorises={['admin']}>
            <Utilisateurs />
          </ProtectionRole>
        )}
        {pageCourante === 'parametres' && (
          <ProtectionRole rolesAutorises={['admin']}>
            <Parametres />
          </ProtectionRole>
        )}
        {pageCourante === 'securite' && <Securite />}
        {pageCourante === 'fournisseurs' && (
  <ProtectionRole rolesAutorises={['admin', 'gerant']}>
    <Fournisseurs />
  </ProtectionRole>
)}
{pageCourante === 'rapports' && (
  <ProtectionRole rolesAutorises={['admin', 'gerant']}>
    <Rapports />
  </ProtectionRole>
)}
{pageCourante === 'rapport' && (
  <ProtectionRole rolesAutorises={['admin', 'gerant']}>
    <RapportCaisse />
  </ProtectionRole>
)}
{pageCourante === 'commandes' && (
  <ProtectionRole rolesAutorises={['admin', 'gerant']}>
    <BonsCommande />
  </ProtectionRole>
)}

      </div>
    </div>
  );
}

export default Dashboard;